import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { LoadingSpinner } from '@honestica/curecall-ui'
import { useState } from 'react'
import { PatientService } from 'src/services/patient.service'
import { twMerge } from 'src/utils/tw-merge.util'
import { useParams } from 'react-router-dom'
import { useNotification } from 'src/providers/notification.provider'
import { useTranslation } from 'react-i18next'
import { PatientDocument, PatientDocumentStatus } from './document.type'
import TimezoneAwareTime from 'src/components/timezone-aware-time/timezone-aware-time.component'
import { useQuery } from '@tanstack/react-query'

/**
 * Cette fonction est trop globale et doit être remplacée par un hook spécialisé
 * @deprecated
 * @param projectId
 * @param patientId
 * @returns
 */
const usePatientDocuments = (projectId: number, patientId: number) => {
  return useQuery<PatientDocument[]>({
    queryKey: ['patient-documents', projectId, patientId],
    queryFn: () => PatientService.getDocuments(projectId, patientId),
  })
}

const PatientDocumentsList = () => {
  const [isDownloading, setIsDownloading] = useState(false)

  const { projectId, patientId } = useParams()
  const { notify } = useNotification()
  const { t } = useTranslation()

  const patientDocumentsQuery = usePatientDocuments(Number(projectId), Number(patientId))

  const PatientDocumentsStatusTrad: Record<PatientDocumentStatus, string> = {
    sent: t('components.documents.awaiting-signature'),
    cancelled: t('components.documents.cancelled'),
    created: t('components.documents.awaiting-signature'),
    declined: t('components.documents.refused'),
    expired: t('components.documents.expired'),
    edited: t('components.documents.modified'),
    signed: t('components.documents.signed'),
    viewed: t('components.documents.viewed-and-awaiting-signature'),
  }

  if (patientDocumentsQuery.isLoading) {
    return (
      <div className="c-py-5">
        <LoadingSpinner size="sm" />
      </div>
    )
  }

  if (patientDocumentsQuery.isError) {
    return <ErrorQueryHandler error={patientDocumentsQuery.error} />
  }

  const downloadDocument = async (documentId: number, name: string) => {
    setIsDownloading(true)

    try {
      const data = await PatientService.downloadDocument(Number(projectId), Number(patientId), documentId)

      const blob = new Blob([data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      document.body.appendChild(a)

      a.href = url
      a.download = `${name}.pdf`
      a.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 0)

      setIsDownloading(false)
    } catch (e) {
      notify({ title: t('common.actions.error'), message: e, appearance: 'ERROR' })
      setIsDownloading(false)
    }
  }

  return (
    <div className="c-mt-8 c-flow-root">
      <div className="c--my-2 c--mx-6 c-overflow-x-auto">
        <div className="c-inline-block c-min-w-full c-py-2 c-align-middle c-px-6">
          <table
            className={twMerge(
              'c-min-w-full c-divide-y  c-divide-gray-300',
              patientDocumentsQuery.data?.length === 0 && 'c-border-b c-border-gray-300',
            )}
          >
            <thead>
              <tr>
                <th scope="col" className="c-py-3.5 c-pr-3 c-text-left c-text-sm c-font-bold c-pl-0">
                  {t('components.documents.documents')}
                </th>
                <th scope="col" className="c-py-3.5 c-px-3 c-text-left c-text-sm c-font-bold">
                  {t('components.documents.state')}
                </th>
                <th scope="col" className="c-py-3.5 c-px-3 c-text-left c-text-sm c-font-bold">
                  {t('components.documents.date')}
                </th>
                <th scope="col" className="c-py-3.5 c-px-3 c-text-left c-text-sm c-font-bold">
                  {t('components.documents.action')}
                </th>
              </tr>
            </thead>
            <tbody className="c-divide-y c-divide-gray-200">
              {patientDocumentsQuery.data?.map((doc) => (
                <tr key={doc.id}>
                  <td className="c-whitespace-nowrap c-py-4 c-pr-3 c-text-sm">{doc.Document.name}</td>
                  <td className="c-whitespace-nowrap c-py-4 c-px-3 c-text-sm">
                    {PatientDocumentsStatusTrad[doc.eurosignStatus] ||
                      t('components.documents.awaiting-signature')}
                  </td>
                  <td className="c-whitespace-nowrap c-py-4 c-px-3 c-text-sm">
                    <TimezoneAwareTime date={doc.sendingDate} formatter="dateWithHours" />
                  </td>
                  <td className="c-whitespace-nowrap c-py-4 c-px-3 c-text-sm">
                    {doc.eurosignStatus === 'signed' ? (
                      <button
                        disabled={isDownloading}
                        className={twMerge(
                          'c-text-primary-600',
                          !isDownloading && 'hover:c-underline hover:c-text-primary-700',
                        )}
                        onClick={() => downloadDocument(doc.id, doc.Document.name)}
                      >
                        {isDownloading
                          ? t('components.documents.dowloading')
                          : t('components.documents.download')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default PatientDocumentsList
